/**
 * <s-product-gallery> — one-image-at-a-time product slider.
 *
 * The track is a native scroll-snap container, so touch swipe, trackpad and
 * keyboard scrolling work without JS. This component adds previous/next
 * buttons, a position counter and thumbnail navigation, and keeps them in sync
 * with the visible slide.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap container.
 *   [data-gallery-slide]          — each slide inside the track.
 *   [data-gallery-prev]/[data-gallery-next] — optional arrow buttons.
 *   [data-gallery-thumb][data-index] — optional thumbnail buttons.
 *   [data-gallery-current]        — optional element showing the 1-based index.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    if (!this.track || this.slides.length < 2) return;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.#index + 1), opts);
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)), opts);
    });

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setIndex(this.slides.indexOf(entry.target));
          }
        }
      },
      { root: this.track, threshold: [0.6] }
    );
    this.slides.forEach((slide) => this.#observer.observe(slide));
    this.#setIndex(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  goTo(index) {
    const count = this.slides.length;
    const next = (index + count) % count;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slides[next].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    this.#setIndex(next);
  }

  #setIndex(index) {
    if (index < 0) return;
    this.#index = index;
    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(index + 1);
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
